import { IconProps } from '../components/common/Icon';

export type NavigationItemId =
  | 'familyTrees'
  | 'currentTree'
  | 'members'
  | 'publicTree'
  | 'settings'
  | 'adminPanel'
  | 'logout';

export interface NavigationContext {
  systemRole?: string;
  lastOpenedTreeId?: string;
  activeTreeId?: string;
  canManageMembers: boolean;
  canManagePublicTree?: boolean;
}

export interface NavigationItemDefinition {
  id: NavigationItemId;
  label: string;
  icon: IconProps['name'];
  getPath?: (context: NavigationContext) => string;
  isVisible: (context: NavigationContext) => boolean;
  isAction?: boolean;
  isActive?: (pathname: string, context: NavigationContext) => boolean;
  sortOrder?: number;
  featureFlag?: string;
  isEnabled?: boolean;
}

const resolveTreeId = (context: NavigationContext) => {
  return context.activeTreeId || context.lastOpenedTreeId || '';
};

export const navigationItems: NavigationItemDefinition[] = [
  {
    id: 'familyTrees',
    label: 'Family Trees',
    icon: 'home',
    getPath: () => '/trees',
    isVisible: () => true,
    isActive: (pathname) => pathname === '/trees',
    sortOrder: 10,
    isEnabled: true,
  },
  {
    id: 'currentTree',
    label: 'Current Tree',
    icon: 'share',
    getPath: (context) => `/canvas/${resolveTreeId(context)}`,
    isVisible: () => false,
    isActive: (pathname, context) => {
      const treeId = resolveTreeId(context);
      return Boolean(treeId) && pathname === `/canvas/${treeId}`;
    },
    sortOrder: 20,
    isEnabled: true,
  },
  {
    id: 'members',
    label: 'Members',
    icon: 'users',
    getPath: (context) => `/tree/${resolveTreeId(context)}/members`,
    isVisible: () => false,
    isActive: (pathname, context) => {
      const treeId = resolveTreeId(context);
      return Boolean(treeId) && pathname === `/tree/${treeId}/members`;
    },
    sortOrder: 30,
    isEnabled: true,
  },
  {
    id: 'publicTree',
    label: 'Public Tree',
    icon: 'share',
    getPath: (context) => `/tree/${resolveTreeId(context)}/public`,
    isVisible: () => false,
    isActive: (pathname, context) => {
      const treeId = resolveTreeId(context);
      return Boolean(treeId) && pathname === `/tree/${treeId}/public`;
    },
    sortOrder: 35,
    isEnabled: true,
  },
  {
    id: 'settings',
    label: 'Settings',
    icon: 'settings',
    getPath: () => '/settings',
    isVisible: () => true,
    isActive: (pathname) => pathname === '/settings',
    sortOrder: 40,
    isEnabled: true,
  },
  {
    id: 'adminPanel',
    label: 'Admin Panel',
    icon: 'lock',
    getPath: () => '/admin',
    isVisible: (context) => context.systemRole === 'Administrator',
    isActive: (pathname) => pathname === '/admin',
    sortOrder: 50,
    isEnabled: true,
  },
  {
    id: 'logout',
    label: 'Logout',
    icon: 'logout',
    isVisible: () => true,
    isAction: true,
    isActive: () => false,
    sortOrder: 60,
    isEnabled: true,
  },
];

export const getVisibleNavigationItems = (context: NavigationContext) => {
  return navigationItems
    .filter((item) => item.isEnabled !== false)
    .filter((item) => item.isVisible(context))
    .sort((left, right) => (left.sortOrder ?? 0) - (right.sortOrder ?? 0));
};
